import { getPackageVersion } from "../core/package-assets.js";
import { runInit } from "./commands/init.js";
import { runDoctor } from "./commands/doctor.js";
import { runCollectEvidence } from "./commands/collect-evidence.js";
import { runMasterOrchestrator } from "./commands/run.js";
import { runExecute } from "./commands/execute.js";
import { runClean } from "./commands/clean.js";

function printHelp() {
  console.log(`ai-workflow

Usage:
  ai-workflow execute "<request>" [--task=<slug>] [--request="<request>"]
  ai-workflow run --spec-path=<path>
  ai-workflow init [--yes] [--force] [--dry-run] [--no-install] [--no-overwrite] [--claude] [--codex] [--antigravity] [--profile=<profile>]
  ai-workflow validate [--a11y] [--visual-dist=<path>] [--port=<port>]
  ai-workflow collect-evidence [--task=<slug>] [--evidence-policy=<optional|required>] [--mode=<quick|standard|full>] [--dry-run] [--visual-dist=<path>] [--port=<port>]
  ai-workflow doctor
  ai-workflow clean [--yes] [--dry-run] [--purge-agents]

Commands:
  execute           Orchestrate execution of a natural request through the state machine
  run               Proportionate orchestrator with branch safety, validation, and bounded remediation
  init              Install AI workflow defaults (OpenCode). --profile=standard (default) or --profile=full (adds examples)
  collect-evidence  Run observed project validation; persists EVIDENCE.json under required evidence policy
  doctor            Verify local ai-workflow installation
  clean             De-initialize and remove AI Workflow files and symlinks from the project
`);
}

interface ParsedFlags {
  yes: boolean;
  force: boolean;
  dryRun: boolean;
  noInstall: boolean;
  noOverwrite: boolean;
  claude: boolean;
  codex: boolean;
  antigravity: boolean;
  "dev-mode": boolean;
  specPath?: string;
  profile?: string;
  taskSlug?: string;
  mode?: string;
  evidencePolicy?: "optional" | "required";
  request?: string;
  visualDist?: string;
  port?: string;
  a11y: boolean;
  purgeAgents?: boolean;
}

function parseFlags(args: string[]): ParsedFlags {
  const specPathArg = args.find((arg) => arg.startsWith("--spec-path="));
  const profileEqArg = args.find((arg) => arg.startsWith("--profile="));
  const taskArg = args.find((arg) => arg.startsWith("--task="));
  const modeArg = args.find((arg) => arg.startsWith("--mode="));
  const evidencePolicyArg = args.find((arg) => arg.startsWith("--evidence-policy="));
  const requestArg = args.find((arg) => arg.startsWith("--request="));
  const visualDistArg = args.find((arg) => arg.startsWith("--visual-dist="));
  const portArg = args.find((arg) => arg.startsWith("--port="));
  const profileIdx = args.indexOf("--profile");
  const profileVal = profileEqArg
    ? profileEqArg.replace("--profile=", "")
    : profileIdx >= 0 && profileIdx + 1 < args.length && !args[profileIdx + 1].startsWith("--")
      ? args[profileIdx + 1]
      : undefined;

  return {
    yes: args.includes("--yes"),
    force: args.includes("--force"),
    dryRun: args.includes("--dry-run"),
    noInstall: args.includes("--no-install"),
    noOverwrite: args.includes("--no-overwrite"),
    claude: args.includes("--claude"),
    codex: args.includes("--codex"),
    antigravity: args.includes("--antigravity"),
    "dev-mode": args.includes("--dev-mode"),
    specPath: specPathArg ? specPathArg.replace("--spec-path=", "") : undefined,
    profile: profileVal || undefined,
    taskSlug: taskArg ? taskArg.replace("--task=", "") : undefined,
    mode: modeArg ? modeArg.replace("--mode=", "") : undefined,
    evidencePolicy: evidencePolicyArg ? (evidencePolicyArg.replace("--evidence-policy=", "") as any) : undefined,
    request: requestArg ? requestArg.replace("--request=", "") : undefined,
    visualDist: visualDistArg ? visualDistArg.replace("--visual-dist=", "") : undefined,
    port: portArg ? portArg.replace("--port=", "") : undefined,
    a11y: args.includes("--a11y"),
    purgeAgents: args.includes("--purge-agents") || args.includes("--purge")
  };
}

type CommandHandler = (args: string[], flags: ParsedFlags) => Promise<void>;

const commandMap: Record<string, CommandHandler> = {
  execute: async (args, flags) => {
    const positionals = args.filter((arg) => !arg.startsWith("-"));
    const request = flags.request || positionals.join(" ");
    const result = await runExecute({
      cwd: process.cwd(),
      naturalRequest: request,
      taskSlug: flags.taskSlug
    });
    if (result && (result.overallStatus === "FAIL_QUALITY_GATE" || result.overallStatus === "BLOCKED" || result.overallStatus === "FAIL")) {
      process.exit(1);
    }
  },
  run: async (_, flags) => {
    await runMasterOrchestrator({
      cwd: process.cwd(),
      ...flags
    });
  },
  init: async (_, flags) => {
    await runInit({
      cwd: process.cwd(),
      ...flags
    });
  },
  "collect-evidence": async (_, flags) => {
    if (flags.mode && !["quick", "standard", "full"].includes(flags.mode)) {
      throw new Error("--mode must be quick, standard, or full");
    }
    await runCollectEvidence({ 
      cwd: process.cwd(), 
      taskSlug: flags.taskSlug, 
      mode: flags.mode, 
      evidencePolicy: flags.evidencePolicy,
      dryRun: flags.dryRun,
      visualDist: flags.visualDist,
      port: flags.port
    });
  },
  validate: async (_, flags) => {
    const { runValidate } = await import("./commands/validate.js");
    await runValidate({
      cwd: process.cwd(),
      taskSlug: flags.taskSlug,
      a11y: flags.a11y,
      visualDist: flags.visualDist,
      port: flags.port
    });
  },
  doctor: async () => {
    await runDoctor({ cwd: process.cwd() });
  },
  clean: async (_, flags) => {
    await runClean({
      cwd: process.cwd(),
      ...flags
    });
  }
};

export async function runCli(args: string[]): Promise<void> {
  const [command] = args;

  if (command === "--version" || command === "-v") {
    console.log(getPackageVersion() || "unknown");
    return;
  }

  if (!command || command === "--help" || command === "-h") {
    printHelp();
    return;
  }

  const handler = commandMap[command];
  if (handler) {
    const argsSlice = args.slice(1);
    const flags = parseFlags(argsSlice);
    await handler(argsSlice, flags);
    return;
  }

  throw new Error(`unknown command: ${command}`);
}

runCli(process.argv.slice(2)).catch((error) => {
  console.error(`ai-workflow error: ${error.message}`);
  process.exit(1);
});
